import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface FieldWrapperProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  id?: string;
  className?: string;
}

export function FieldWrapper({ label, hint, error, required, id, children, className }: FieldWrapperProps) {
  const hintId = id ? `${id}-hint` : undefined;
  const errorId = id ? `${id}-error` : undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-fg">
          {label}
          {required && <span className="ml-0.5 text-gold-500">*</span>}
        </label>
      )}
      {children}
      {hint && !error && (
        <p id={hintId} className="text-xs text-fg-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, leftIcon, rightIcon, ...props }, ref) => (
    <div
      className={cn(
        'flex h-11 items-center gap-2 rounded-md border bg-bg-elevated px-3 text-sm text-fg transition-colors',
        'focus-within:border-gold-500/60 focus-within:ring-2 focus-within:ring-gold-500/20',
        invalid ? 'border-danger/60' : 'border-border',
        className,
      )}
    >
      {leftIcon && <span className="text-fg-subtle">{leftIcon}</span>}
      <input
        ref={ref}
        className="flex-1 bg-transparent outline-none placeholder:text-fg-subtle disabled:cursor-not-allowed disabled:opacity-50"
        {...props}
      />
      {rightIcon && <span className="text-fg-subtle">{rightIcon}</span>}
    </div>
  ),
);
Input.displayName = 'Input';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        'flex min-h-[120px] w-full rounded-md border bg-bg-elevated px-3 py-3 text-sm text-fg transition-colors',
        'placeholder:text-fg-subtle focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20',
        'disabled:cursor-not-allowed disabled:opacity-50',
        invalid ? 'border-danger/60' : 'border-border',
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';